'use client';

import { IconStrokeWidth } from '@actr/icons';
import { IconBell } from '@actr/icons/bell';
import { IconCity } from '@actr/icons/city';
import { IconExtraLuggage } from '@actr/icons/extra-luggage';
import { IconGlobe } from '@actr/icons/globe';
import { IconInsuranceFlight } from '@actr/icons/insurance-flight';
import { IconInsuranceMedical } from '@actr/icons/insurance-medical';
import { IconInsuranceRefund } from '@actr/icons/insurance-refund';
import { IconOnlineCheckin } from '@actr/icons/online-checkin';
import { IconPlaneDeparture } from '@actr/icons/plane-departure';
import { IconReset } from '@actr/icons/reset';
import { IconTrainV2 } from '@actr/icons/train-v2';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { FlexAlight, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { Card } from '@actr/ui/src/components/card';
import { Container } from '@actr/ui/src/components/container';
import { AkRedesignLogo } from '@actr/widgets/src/components/_misc/site-loader/_parts/ak-redesign-logo';
import React from 'react';

import * as Styled from './style.css';

const AboutPage: React.FC = () => {
  const extraServices = [{
    icon: IconInsuranceFlight,
    content: '<b>Страховка на время перелета:</b> отмена или задержка рейса'
  }, {
    icon: IconInsuranceRefund,
    content: '<b>Страхование имущества:</b> компенсация потери багажа'
  }, {
    icon: IconReset,
    content: '<b>Возврат билета:</b> удобная функция прямо в личном кабинете'
  }, {
    icon: IconExtraLuggage,
    content: 'Возможность <b>докупить багаж</b> уже после оформления билета'
  }, {
    icon: IconBell,
    content: '<b>Уведомления:</b> сообщение о задержке или отмене рейса по SMS'
  }, {
    icon: IconOnlineCheckin,
    content: '<b>Онлайн регистрация</b> на рейс экономит время в аэропорту'
  }];

  function renderList(title: string, items: string[]) {
    return (
      <>
        {!!title && (
          <>
            <Text font="paragraph.m">
              {title}
            </Text>
            <Space size={Sizes.X2S} />
          </>
        )}
        <ul className={Styled.List}>
          {items.map((item, index) => (
            <li key={index}>
              <Text font="paragraph.m">
                {item}
              </Text>
            </li>
          ))}
        </ul>
      </>
    );
  }

  function renderPeriodTitle(years: string, title: string) {
    return (
      <>
        <div className={Styled.PeriodTitle}>
          <Text className={Styled.Badge} font="title.l">
            {years}
          </Text>
          <Text font="title.l">
            {title}
          </Text>
        </div>
        <Space size={Sizes.X2S} />
      </>
    );
  }

  return (
    <Container className={Styled.Container}>
      <Text font="title.x4l" tagName="h1">
        О компании Aviakassa
      </Text>
      <Space size={Sizes.M} />
      <div className={Styled.Tiles}>
        <Card>
          <AkRedesignLogo className={Styled.AviakassaLogo} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m.bold">
            Мы — сервис онлайн бронирования авиабилетов, аккредитованный член IATA и транспортной клиринговой палаты
          </Text>
        </Card>
        <Card>
          <IconGlobe className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Ищем билеты по всем авиакомпаниям мира, используя интеграцию с GDS (глобальные системы бронирования)
          </Text>
        </Card>
        <Card>
          <IconTrainV2 className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Предоставляем все направления РЖД по России, включая направления за рубеж
          </Text>
        </Card>
        <Card>
          <IconPlaneDeparture className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Находим лучшие предложения от авиакомпаний и сотрудничаем с 180 лоукостерами
          </Text>
        </Card>
        <Card>
          <IconCity className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Более миллиона отелей, хостелов и апартаментов по всему миру
          </Text>
        </Card>
        <Card>
          <IconOnlineCheckin className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Предлагаем чартерные рейсы из России по 115 направлениям в 50 стран мира
          </Text>
        </Card>
        <Card>
          <IconInsuranceMedical className={Styled.SmallIcon} />
          <Space size={Sizes.S} />
          <Text font="paragraph.m">
            Все виды страховок для путешественников от наших партнеров по спец. тарифам
          </Text>
        </Card>
      </div>
      <Space size={Sizes.M} />
      <Card>
        <Text font="paragraph.m">
          В настоящее время представленный Сервис онлайн-бронирования - это аккредитованный в IATA (международная ассоциация воздушного
          транспорта), а так же ТКП (транспортная клиринговая палата) агент по реализации авиаперевозок. Много лет мы с помощью передовых
          технологий помогаем спланировать отдых или рабочую поездку в любую точку мира. Запланированный отпуск или спонтанные выходные – у
          нас вы найдете лучшие варианты по самым выгодным ценам.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      <Card>
        <Text font="title.l">
          Наша миссия
        </Text>
        <Space size={Sizes.X2S} />
        <Text font="paragraph.m">
          Мы верим, что планирование путешествия не должно быть сложным или утомительным. С Aviakassa.com вы получаете не только инструменты
          для бронирования, но и надежного партнера, который будет с вами на каждом этапе поездки. Мы создаем сервисы, которые ставят ваши
          интересы на первое место, помогая превратить сложные маршруты в простые решения.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      <Card>
        <Text font="title.l">
          Почему выбирают нас?
        </Text>
        <Space size={Sizes.X2S} />
        {renderList('', [
          'Мы экономим ваше время: удобный интерфейс позволяет найти и забронировать все необходимое за пару кликов.',
          'Мы делаем ваш отдых доступным: оптимизированная система поиска всегда находит лучшие предложения и выгодные цены.',
          'Мы всегда рядом: служба поддержки работает круглосуточно, чтобы ответить на ваши вопросы и помочь в любой ситуации.',
          'Мы стремимся к совершенству: в нашей команде более 100 разработчиков, которые постоянно совершенствует систему поиска и '
            + 'бронирования, внедряя новые функции и технологии, чтобы сделать ваше путешествие еще более комфортным.'
        ])}
      </Card>
      <Space size={Sizes.M} />
      <Text font="title.x2l">
        История нашего становления
      </Text>
      <Space size={Sizes.M} />
      {renderPeriodTitle('2015', 'Основание и запуск')}
      <Card>
        <Text font="paragraph.m">
          Компания была основана в 2015 году группой IT-специалистов и авиаэкспертов, увидевших растущую потребность в цифровизации рынка
          авиаперевозок. На тот момент рынок был насыщен традиционными агентствами, работающими по устаревшим схемам.
        </Text>
        <Space size={Sizes.S} />
        {renderList('Основной идеей было создать онлайн-платформу, которая:', [
          'интегрирует предложения от авиакомпаний, железных дорог и гостиниц в одном окне;',
          'позволяет пользователю самостоятельно бронировать билеты без необходимости обращаться в офис;',
          'делает упор на удобство, скорость и прозрачность.'
        ])}
        <Space size={Sizes.S} />
        <Text font="paragraph.m">
          Первый MVP был развернут за 6 месяцев и включал только авиабилеты по России. Уже в 2016 году была достигнута договоренность с
          рядом лоукостеров и начато подключение международных маршрутов.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      {renderPeriodTitle('2016 - 2019', 'Рост и выход на международный уровень')}
      <Card>
        {renderList('С 2016 года компания начала стремительно расширяться:', [
          'получила аккредитацию IATA (что позволило работать напрямую с авиакомпаниями);',
          'начала подключение к GDS-системам (Amadeus, Sabre, Galileo);',
          'был запущен раздел бронирования отелей и железнодорожных билетов.'
        ])}
      </Card>
      <Space size={Sizes.M} />
      {renderPeriodTitle('2017', 'Корпоративный сегмент и автоматизация')}
      <Card>
        {renderList('В этот период компания выходит на рынок B2B:', [
          'запускается личный кабинет для корпоративных клиентов, позволяющий организациям централизованно планировать командировки, '
            + 'управлять лимитами и получать закрывающие документы;',
          'внедряются инструменты динамического ценообразования и рекомендаций на основе поведенческих паттернов пользователей.'
        ])}
        <Space size={Sizes.S} />
        <Text font="paragraph.m">
          Также начинается интеграция с внешними CRM и ERP системами, что делает сервис удобным не только для физических лиц, но и для
          малого и среднего бизнеса.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      {renderPeriodTitle('2020–2021', 'Испытание пандемией')}
      <Card>
        {renderList(
          'Пандемия COVID-19 стала серьёзным вызовом для всей отрасли. Компания использовала этот период для внутренней трансформации:',
          [
            'оптимизированы процессы возврата и отмены билетов;',
            'запущены инструменты уведомлений о санитарных ограничениях и правилах въезда;',
            'команда сделала упор на лояльность и поддержку клиентов, что позволило сохранить ядро аудитории.'
          ]
        )}
        <Space size={Sizes.S} />
        <Text font="paragraph.m">
          Компания также перешла на гибридную модель работы и сократила операционные издержки на 30%.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      {renderPeriodTitle('2022–2025', 'Технологическая трансформация')}
      <Card>
        <Text font="paragraph.m">
          В 2023 году компания стала владельцем онлайн сервиса Aviakassa. И уже в 2024 году Aviakassa вошла в топ самых популярных
          онлайн-сервисов.
        </Text>
        <Space size={Sizes.S} />
        {renderList('В постпандемийный период Aviakassa делает ставку на инновации:', [
          'запускается собственная система рекомендаций;',
          'усиливается мобильное направление: в приложение добавлена офлайн-доступность билетов, тревел-чеклисты, push-уведомления об '
            + 'изменениях рейсов;',
          'реализована интеграция с туристическими страховыми сервисами, трансферами и такси;',
          'активно развивается SEO, контекстная реклама и аккаунты Aviakassa в соцсетях.'
        ])}
        <Space size={Sizes.S} />
        <Text font="paragraph.m">
          В 2024 году Aviakassa входит в топ-5 самых популярных агрегаторов авиабилетов в России, по версии TravelData.ru, обрабатывая более
          1 миллиона запросов в сутки.
        </Text>
      </Card>
      <Space size={Sizes.M} />
      <Text font="title.x2l">
        Авиабилеты
      </Text>
      <Space size={Sizes.S} />
      <Card>
        <div className={Styled.Flex}>
          <IconPlaneDeparture className={Styled.BigIcon} strokeWidth={IconStrokeWidth.LIGHT} />
          <Text font="paragraph.m">
            Ищем билеты по всем авиакомпаниям мира, используя интеграцию с GDS (глобальные системы бронирования) и NDC (новый стандарт
            передачи данных с поставщиками).
          </Text>
          <Text font="paragraph.m">
            Предлагаем чартерные рейсы из России по 115 направлениям в 50 стран мира, сотрудничаем с 180 лоукостерами.
          </Text>
          <Text font="paragraph.m">
            Все классы обслуживания, самые быстрые и дешевые перелеты – вы без труда найдете подходящий вариант и сможете состыковать рейсы
            разных авиакомпаний.
          </Text>
        </div>
      </Card>
      <Space size={Sizes.M} />
      <Text font="title.x2l">
        Ж/Д билеты
      </Text>
      <Space size={Sizes.S} />
      <Card>
        <div className={Styled.Flex}>
          <IconTrainV2 className={Styled.BigIcon} strokeWidth={IconStrokeWidth.LIGHT} />
          <Text font="paragraph.m">
            Предоставляем все направления РЖД по России и за рубеж, включая фирменные и скоростные поезда. Простой и удобный поиск, никаких
            скрытых комиссий!
          </Text>
        </div>
      </Card>
      <Space size={Sizes.M} />
      <Text font="title.x2l">
        Отели, хостелы и апартаменты
      </Text>
      <Space size={Sizes.S} />
      <Card>
        <div className={Styled.Flex}>
          <IconCity className={Styled.BigIcon} strokeWidth={IconStrokeWidth.LIGHT} />
          <Text font="paragraph.m">
            Более миллиона отелей, хостелов и апартаментов по всему миру. Выбирайте нужный вариант по цене, количеству звезд и
            местоположению – на нашем сайте вы можете полностью спланировать свою поездку!
          </Text>
        </div>
      </Card>
      <Space size={Sizes.M} />
      <Text font="title.x2l">
        Дополнительные услуги
      </Text>
      <Space size={Sizes.S} />
      <div className={Styled.Grid}>
        {extraServices.map((item, index) => (
          <Card key={index}>
            <FlexWrapper alight={FlexAlight.CENTER} gap={Sizes.S}>
              {React.createElement(item.icon, {
                className: Styled.BigIcon,
                strokeWidth: IconStrokeWidth.LIGHT
              })}
              <Text font="paragraph.m">
                <div dangerouslySetInnerHTML={{ __html: item.content }} />
              </Text>
            </FlexWrapper>
          </Card>
        ))}
      </div>
    </Container>
  );
};

export default AboutPage;
